import { FormEvent, useEffect, useState } from 'react'
import api from '../../shared/api/request'

type ApiResult<T> = { success: boolean; data: T; error?: string; message?: string }
type BillingOrder = {
  id: string
  departmentName?: string
  schoolName?: string
  status: string
  amountCents: number
  requestedByName?: string
  requestedAt: string
  proofAvailable?: boolean
  proofName?: string
  submittedAt?: string | null
  reviewedAt?: string | null
  reviewNote?: string
  activatedAt?: string | null
  paidUntil?: string | null
}
type BillingRefund = {
  id: string
  order_id: string
  departmentName: string
  status: string
  amount_cents: number
  reason: string
  requested_at: string
  review_note?: string
}
type BillingDepartment = { id: string; displayName: string; schoolName?: string; billingEnabled?: boolean; subscriptionStatus?: string }
type Plan = {
  subscriptionStatus: string
  verificationStatus: string
  trialStartedAt: string | null
  trialExpiresAt: string | null
  paidUntil: string | null
  memberLimit: number
  memberCount: number
  storageQuotaBytes: number
  billingEligible: boolean
  readOnly: boolean
  priceYuan: number
  formalMemberLimit: number
  formalStorageQuotaBytes: number
  orders: BillingOrder[]
}

const statusText: Record<string, string> = {
  legacy: '历史正式部门',
  trial_active: '免费试用中',
  trial_readonly: '试用已到期（只读）',
  activation_pending: '开通处理中',
  formal: '正式套餐',
  expired: '正式套餐已到期',
  awaiting_proof: '待上传付款凭证',
  pending_review: '等待平台审核',
  approved: '已开通',
  rejected: '未通过',
  refunded: '已退款',
  pending: '待处理'
}

const dateText = (value?: string | null) => value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-'
const bytesText = (value: number) => value >= 1024 ** 3 ? `${(value / 1024 ** 3).toFixed(1)} GB` : `${Math.round(value / 1024 ** 2)} MB`
const errorText = (error: unknown) => (error as { message?: string; error?: string })?.message || (error as { error?: string })?.error || '操作失败，请稍后重试。'

export function DepartmentBilling() {
  const [plan, setPlan] = useState<Plan | null>(null)
  const [proof, setProof] = useState<File | null>(null)
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    try {
      const result = await api.get('/billing/plan') as unknown as ApiResult<Plan>
      setPlan(result.data)
    } catch (err) { setError(errorText(err)) }
  }
  useEffect(() => { void load() }, [])

  async function createOrder() {
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post('/billing/orders')
      setMessage('开通申请已创建，请按页面提示完成付款并上传凭证。')
      await load()
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function uploadProof(event: FormEvent) {
    event.preventDefault()
    if (!proof || !consent || !plan) return
    const order = plan.orders.find(item => item.status === 'awaiting_proof')
    if (!order) return
    setBusy(true); setError(''); setMessage('')
    try {
      const proofBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('凭证读取失败。'))
        reader.readAsDataURL(proof)
      })
      await api.post(`/billing/orders/${order.id}/proof`, { proofBase64, proofName: proof.name, sensitiveConsent: true })
      setProof(null); setConsent(false); setMessage('付款凭证已提交，平台审核完成后会在这里显示结果。')
      await load()
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function requestRefund(order: BillingOrder) {
    const reason = window.prompt('请填写退款原因（3至500字）')?.trim()
    if (!reason) return
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post(`/billing/orders/${order.id}/refund-request`, { reason })
      setMessage('退款申请已提交，请等待平台人工处理。')
      await load()
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  if (!plan) return <div className="loading">正在读取部门套餐…</div>
  const openOrder = plan.orders.find(item => ['awaiting_proof', 'pending_review'].includes(item.status))
  const activeOrder = plan.orders.find(item => item.status === 'approved')
  const refundAvailable = activeOrder?.activatedAt && Date.now() - new Date(activeOrder.activatedAt).getTime() <= 7 * 86400000

  return <div className="page-stack commercial-page">
    <div className="page-title"><div><h1>套餐与开通</h1><p>查看试用期限、成员和存储额度。付款功能仅向受邀试点部门开放。</p></div><span className={`plan-state ${plan.readOnly ? 'warning' : ''}`}>{statusText[plan.subscriptionStatus] || plan.subscriptionStatus}</span></div>
    {message && <div className="alert">{message}</div>}{error && <div className="alert error">{error}</div>}
    <section className="commercial-summary">
      <div><span>当前成员</span><strong>{plan.memberCount} / {plan.memberLimit}</strong></div>
      <div><span>存储额度</span><strong>{bytesText(plan.storageQuotaBytes)}</strong></div>
      <div><span>{plan.subscriptionStatus === 'formal' ? '正式有效期' : '试用截止'}</span><strong>{dateText(plan.subscriptionStatus === 'formal' ? plan.paidUntil : plan.trialExpiresAt)}</strong></div>
    </section>
    {plan.readOnly && <div className="commercial-readonly"><strong>当前部门处于只读状态</strong><p>成员仍可查看和导出数据，但不能新增或修改业务记录。部长可联系平台管理员申请继续试点。</p></div>}
    <section className="commercial-offer">
      <div><h2>试运营正式套餐</h2><p>适合已完成试用、需要继续协作的受邀部门。不自动续费，平台人工审核后生效。</p><ul><li>{plan.formalMemberLimit} 名有效成员</li><li>{bytesText(plan.formalStorageQuotaBytes)} 部门存储</li><li>成员、任务、排班、活动、审批、财务与资料交接</li></ul></div>
      <div className="commercial-price"><strong>¥{plan.priceYuan}</strong><span>/ 年</span>{!plan.billingEligible && <small>当前未进入人工付费白名单</small>}</div>
    </section>
    {plan.billingEligible && !openOrder && !['legacy', 'formal'].includes(plan.subscriptionStatus) && <button className="primary commercial-start" disabled={busy} onClick={() => void createOrder()}>申请人工开通</button>}
    {openOrder?.status === 'awaiting_proof' && <section className="commercial-payment"><div><h2>完成付款并提交凭证</h2><p>收款码仅对当前部门部长显示。付款前请核对金额，凭证应遮盖无关订单号、余额和其他个人信息。</p><a className="secondary button-link" href="/api/billing/payment-qr" target="_blank" rel="noreferrer">查看受保护的收款码</a></div><form onSubmit={uploadProof}><label>付款凭证<input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => setProof(event.target.files?.[0] || null)} required /></label><label className="consent-check"><input type="checkbox" checked={consent} onChange={event => setConsent(event.target.checked)} /><span>我已遮盖无关订单号、账户余额和其他敏感信息，并同意仅用于人工核验本次付款。</span></label><button className="primary" disabled={busy || !proof || !consent}>{busy ? '正在提交…' : '提交付款凭证'}</button></form></section>}
    {openOrder?.status === 'pending_review' && <div className="commercial-readonly"><strong>付款凭证审核中</strong><p>提交于 {dateText(openOrder.submittedAt)}。请勿重复付款，审核结果会保留在本页。</p></div>}
    {refundAvailable && activeOrder && <button className="text-btn commercial-refund" disabled={busy} onClick={() => void requestRefund(activeOrder)}>开通后7日内申请全额退款</button>}
    <section className="panel"><div className="panel-head"><h2>开通记录</h2></div><div className="table-wrap"><table><thead><tr><th>申请时间</th><th>金额</th><th>状态</th><th>有效期</th><th>审核说明</th></tr></thead><tbody>{plan.orders.map(order => <tr key={order.id}><td>{dateText(order.requestedAt)}</td><td>¥{(order.amountCents / 100).toFixed(2)}</td><td>{statusText[order.status] || order.status}</td><td>{dateText(order.paidUntil)}</td><td>{order.reviewNote || '-'}</td></tr>)}{!plan.orders.length && <tr><td colSpan={5}>暂无开通记录</td></tr>}</tbody></table></div></section>
  </div>
}

export function PlatformBilling() {
  const [orders, setOrders] = useState<BillingOrder[]>([])
  const [refunds, setRefunds] = useState<BillingRefund[]>([])
  const [departments, setDepartments] = useState<BillingDepartment[]>([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const [orderResult, refundResult, overviewResult] = await Promise.all([
        api.get('/platform/billing/orders') as unknown as Promise<ApiResult<BillingOrder[]>>,
        api.get('/platform/billing/refunds') as unknown as Promise<ApiResult<BillingRefund[]>>,
        api.get('/platform/overview') as unknown as Promise<ApiResult<{ departments: BillingDepartment[] }>>
      ])
      setOrders(orderResult.data); setRefunds(refundResult.data); setDepartments(overviewResult.data.departments || [])
    } catch (err) { setError(errorText(err)) }
  }
  useEffect(() => { void load() }, [])

  async function review(kind: 'orders' | 'refunds', id: string, action: 'approve' | 'reject') {
    const reviewNote = window.prompt(action === 'approve' ? '审核备注（可留空）' : '请填写驳回原因') || ''
    if (action === 'reject' && reviewNote.trim().length < 3) return
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post(`/platform/billing/${kind}/${id}/review`, { action, reviewNote })
      setMessage(action === 'approve' ? '审核已通过。' : '申请已驳回。')
      await load()
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function toggleBilling(department: BillingDepartment) {
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post(`/platform/departments/${department.id}/billing-access`, { enabled: !department.billingEnabled })
      setMessage(department.billingEnabled ? '已停止该部门提交新的付费申请。' : '该部门已加入人工付费试点白名单。')
      await load()
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  return <div className="platform-billing-page">
    {message && <div className="alert">{message}</div>}{error && <div className="alert error">{error}</div>}
    <section className="panel"><div className="panel-head"><div><h2>人工开通白名单</h2><p>只有白名单部门能看到收款和凭证入口；服务器未配置收款资料时仍不会开放付款。</p></div></div><div className="table-wrap"><table><thead><tr><th>学校 / 部门</th><th>当前套餐</th><th>人工付费入口</th><th></th></tr></thead><tbody>{departments.map(department => <tr key={department.id}><td><strong>{department.displayName}</strong><small>{department.schoolName || '-'}</small></td><td>{statusText[department.subscriptionStatus || ''] || department.subscriptionStatus || '-'}</td><td>{department.billingEnabled ? '允许申请' : '未开放'}</td><td><button className={department.billingEnabled ? 'danger compact-action' : 'secondary compact-action'} disabled={busy} onClick={() => void toggleBilling(department)}>{department.billingEnabled ? '移出白名单' : '加入白名单'}</button></td></tr>)}{!departments.length && <tr><td colSpan={4}>暂无部门</td></tr>}</tbody></table></div></section>
    <section className="panel"><div className="panel-head"><div><h2>正式套餐开通审核</h2><p>只核对付款凭证和部门信息，不进入部门业务数据。</p></div></div><div className="table-wrap"><table><thead><tr><th>学校 / 部门</th><th>申请人</th><th>金额</th><th>状态</th><th>凭证</th><th>操作</th></tr></thead><tbody>{orders.map(order => <tr key={order.id}><td><strong>{order.departmentName}</strong><small>{order.schoolName}</small></td><td>{order.requestedByName || '-'}</td><td>¥{(order.amountCents / 100).toFixed(2)}</td><td>{statusText[order.status] || order.status}</td><td>{order.proofAvailable ? <a href={`/api/platform/billing/orders/${order.id}/proof`} target="_blank" rel="noreferrer">查看凭证</a> : '未上传'}</td><td>{order.status === 'pending_review' ? <div className="row-actions"><button className="primary compact-action" disabled={busy} onClick={() => void review('orders', order.id, 'approve')}>通过</button><button className="danger compact-action" disabled={busy} onClick={() => void review('orders', order.id, 'reject')}>驳回</button></div> : '-'}</td></tr>)}{!orders.length && <tr><td colSpan={6}>暂无开通申请</td></tr>}</tbody></table></div></section>
    <section className="panel"><div className="panel-head"><h2>退款申请</h2></div><div className="table-wrap"><table><thead><tr><th>部门</th><th>金额</th><th>原因</th><th>状态</th><th>操作</th></tr></thead><tbody>{refunds.map(refund => <tr key={refund.id}><td>{refund.departmentName}</td><td>¥{(Number(refund.amount_cents) / 100).toFixed(2)}</td><td>{refund.reason}</td><td>{statusText[refund.status] || refund.status}</td><td>{refund.status === 'pending' ? <div className="row-actions"><button className="primary compact-action" disabled={busy} onClick={() => void review('refunds', refund.id, 'approve')}>确认退款</button><button className="danger compact-action" disabled={busy} onClick={() => void review('refunds', refund.id, 'reject')}>驳回</button></div> : '-'}</td></tr>)}{!refunds.length && <tr><td colSpan={5}>暂无退款申请</td></tr>}</tbody></table></div></section>
  </div>
}
